// classes/contexts/AuthContext.js

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHostInstitution } from "../../hooks/queries/useHostInstitution";
import { oidcManager } from "../../services/oauth/OAuthService";

import { jwtDecode } from "jwt-decode";
import { removeToken, setToken } from "../utils/TokenUtils";

export const AuthContext = createContext(null);

const defaultClaims = {
  firstName: null,
  lastName: null,
  fullName: null,
  username: null,
  roles: [],
  lang: null,
  raw: {},
};

const mapClaims = token => {
  if (!token) {
    return defaultClaims;
  }
  let c = {};
  try {
    c = jwtDecode(token) || {};
  } catch {}
  const firstName = c.first_name?.trim() || null;
  const lastName = c.last_name?.trim() || null;
  const username = c.username || null;
  const fullName =
    [firstName, lastName].filter(Boolean).join(" ") || username || null;
  const roles = c.role ? [c.role] : [];
  const lang = c.preferred_language || null;
  return { firstName, lastName, fullName, username, roles, lang, raw: c };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getSingleHostInstitution } = useHostInstitution();
  const [claims, setClaims] = useState(defaultClaims);

  const extractTenantInfo = useCallback(
    async token => {
      try {
        const tokenPayload = JSON.parse(atob(token.split(".")[1]));
        const tenantId = tokenPayload?.tenant_id;
        if (tenantId) {
          const { formatted: tenantData } = await getSingleHostInstitution(
            tenantId
          );
          setTenantInfo(tenantData);
        }
      } catch (err) {}
    },
    [getSingleHostInstitution]
  );

  const updateUserState = useCallback(
    async oidcUser => {
      setToken(oidcUser.access_token);
      setUser(oidcUser);
      setClaims(mapClaims(oidcUser.access_token));
      await extractTenantInfo(oidcUser.access_token);
    },
    [extractTenantInfo]
  );

  const resetAuthState = useCallback(() => {
    setUser(null);
    removeToken();
    setTenantInfo(null);
    setClaims(defaultClaims);
  }, []);

  const login = useCallback(() => {
    // Store current path to restore after OAuth redirect
    sessionStorage.setItem("oauth_redirect_path", window.location.pathname);
    oidcManager.signinRedirect();
  }, []);

  const logout = useCallback(() => {
    oidcManager.getUser().then(user => {
      oidcManager.signoutRedirect({ id_token_hint: user?.id_token });
    });
  }, []);

  const postLogout = useCallback(() => {
    resetAuthState();
  }, [resetAuthState]);

  const checkPermissions = useCallback(
    permissions => {
      if (!user || !user.access_token || user.expired) return false;

      if (
        !permissions ||
        (Array.isArray(permissions) && permissions.length === 0)
      ) {
        return true;
      }

      let decodedUser = jwtDecode(user.access_token);
      const userPermissions = decodedUser.permissions;

      if (!userPermissions) {
        return false;
      }

      if (Array.isArray(permissions)) {
        return permissions.every(p => userPermissions.includes(p));
      }

      return userPermissions.includes(permissions);
    },
    [user]
  );

  useEffect(() => {
    oidcManager
      .getUser()
      .then(async oidcUser => {
        if (oidcUser && !oidcUser.expired) {
          await updateUserState(oidcUser);
        } else {
          resetAuthState();
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });

    const onUserLoaded = async newUser => {
      await updateUserState(newUser);
    };

    const onAccessTokenExpiring = () => {
      // No action needed - automaticSilentRenew will handle it
    };

    const onSilentRenewError = error => {
      console.error('[OAuth] Silent token renewal failed:', error);
      // Don't logout immediately - the token might still be valid for a short time
      // Let it expire naturally and user will be prompted to login
    };

    oidcManager.events.addUserLoaded(onUserLoaded);
    oidcManager.events.addAccessTokenExpiring(onAccessTokenExpiring);
    oidcManager.events.addSilentRenewError(onSilentRenewError);

    return () => {
      oidcManager.events.removeUserLoaded(onUserLoaded);
      oidcManager.events.removeAccessTokenExpiring(onAccessTokenExpiring);
      oidcManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, [updateUserState, resetAuthState, logout]);

  const value = useMemo(
    () => ({
      user,
      tenantInfo,
      login,
      logout,
      loading,
      postLogout,
      isAuthenticated: !!user && !user.expired,
      checkPermissions,
      claims,
      rawUser: user,
    }),
    [
      user,
      tenantInfo,
      login,
      logout,
      loading,
      postLogout,
      checkPermissions,
      claims,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
